/**
 * @module components/ACS/ACSConfigPanel
 * @description Panel de configuración del asistente ACS para administradores.
 *
 * Permite personalizar la "persona" del asistente IA:
 * - Nombre del asistente (ej: "ARIA", "CISO-AI")
 * - Color de acento del avatar ACS
 * - Tono de las respuestas (formal, técnico, accesible)
 * - Prompt de personalidad adicional
 *
 * La configuración se persiste en `localStorage` por `organization_id` para
 * que cada organización tenga su propia instancia del asistente.
 *
 * @see ACSAvatar que usa el color de acento configurado aquí
 * @see ACSContext que carga la configuración del localStorage al inicializar
 */
// ============================================================
// ACSConfigPanel — Configuración del asistente ACS
// Permite al admin personalizar el nombre, avatar y comportamiento
// del asistente IA en las sesiones.
// Configuración persiste en localStorage por organización.
// ============================================================

import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Bot, Palette, MessageSquare, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// ──────────────────────────────────────────────────────────
// TIPOS Y CONSTANTES
// ──────────────────────────────────────────────────────────

export interface ACSPersonaConfig {
  assistantName:    string;    // Nombre mostrado en el chat
  avatarColor:      string;    // Color del avatar (hex)
  avatarInitials:   string;    // Iniciales del avatar (máx 3 chars)
  customInstruction: string;   // Instrucción adicional para el system prompt
}

const DEFAULTS: ACSPersonaConfig = {
  assistantName:    'Asistente ACS',
  avatarColor:      '#7c3aed',   // violet-700
  avatarInitials:   'ACS',
  customInstruction: '',
};

const PRESET_COLORS = [
  { hex: '#7c3aed', label: 'Violeta' },
  { hex: '#1d4ed8', label: 'Azul' },
  { hex: '#0891b2', label: 'Cian' },
  { hex: '#059669', label: 'Verde' },
  { hex: '#d97706', label: 'Ámbar' },
  { hex: '#dc2626', label: 'Rojo' },
  { hex: '#1f2937', label: 'Gris oscuro' },
];

function getStorageKey(organizationId: string) {
  return `acs_persona_${organizationId}`;
}

export function loadACSPersonaConfig(organizationId: string): ACSPersonaConfig {
  try {
    const raw = localStorage.getItem(getStorageKey(organizationId));
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

function saveACSPersonaConfig(organizationId: string, config: ACSPersonaConfig) {
  localStorage.setItem(getStorageKey(organizationId), JSON.stringify(config));
}

// ──────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ──────────────────────────────────────────────────────────

export function ACSConfigPanel() {
  const { user } = useAuth();
  const organizationId = user?.organization_id ?? 'demo';

  const [config,  setConfig]  = useState<ACSPersonaConfig>(() => loadACSPersonaConfig(organizationId));
  const [saved,   setSaved]   = useState(false);

  useEffect(() => {
    setConfig(loadACSPersonaConfig(organizationId));
  }, [organizationId]);

  function handleSave() {
    saveACSPersonaConfig(organizationId, config);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function handleReset() {
    const defaults = { ...DEFAULTS };
    setConfig(defaults);
    saveACSPersonaConfig(organizationId, defaults);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function updateField<K extends keyof ACSPersonaConfig>(key: K, value: ACSPersonaConfig[K]) {
    setConfig(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  return (
    <div className="max-w-2xl space-y-6">

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 dark:text-blue-400">
          Personaliza cómo se muestra el asistente IA en las sesiones ACS.
          Los cambios se guardan localmente en este navegador y aplican a todas las sesiones de tu organización.
        </p>
      </div>

      {/* Preview del avatar */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4 text-violet-500" />
          Vista previa del asistente
        </h3>
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold shadow"
            style={{ backgroundColor: config.avatarColor }}
          >
            {config.avatarInitials.slice(0, 3) || 'ACS'}
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{config.assistantName || 'Asistente ACS'}</p>
            <div className="bg-gray-100 dark:bg-gray-600 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 max-w-xs">
              Hola, soy tu asistente de cumplimiento normativo. ¿En qué puedo ayudarte con este control?
            </div>
          </div>
        </div>
      </section>

      {/* Nombre e iniciales */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Bot className="w-4 h-4 text-violet-500" />
          Identidad del asistente
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre del asistente
            </label>
            <input
              value={config.assistantName}
              onChange={e => updateField('assistantName', e.target.value)}
              placeholder="Asistente ACS"
              maxLength={50}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Iniciales del avatar (máx 3)
            </label>
            <input
              value={config.avatarInitials}
              onChange={e => updateField('avatarInitials', e.target.value.toUpperCase().slice(0, 3))}
              placeholder="ACS"
              maxLength={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent font-mono uppercase"
            />
          </div>
        </div>
      </section>

      {/* Color del avatar */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Palette className="w-4 h-4 text-violet-500" />
          Color del avatar
        </h3>

        <div className="flex items-center gap-3 flex-wrap">
          {PRESET_COLORS.map(p => (
            <button
              key={p.hex}
              onClick={() => updateField('avatarColor', p.hex)}
              title={p.label}
              className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                backgroundColor: p.hex,
                borderColor: config.avatarColor === p.hex ? '#fff' : 'transparent',
                boxShadow: config.avatarColor === p.hex ? `0 0 0 2px ${p.hex}` : 'none',
              }}
            />
          ))}
          {/* Color personalizado */}
          <div className="relative">
            <input
              type="color"
              value={config.avatarColor}
              onChange={e => updateField('avatarColor', e.target.value)}
              className="w-8 h-8 rounded-full cursor-pointer border-2 border-gray-300 dark:border-gray-600 opacity-0 absolute inset-0"
              title="Color personalizado"
            />
            <div
              className="w-8 h-8 rounded-full border-2 border-dashed border-gray-400 flex items-center justify-center text-[10px] text-gray-500 font-bold"
              title="Color personalizado"
            >
              +
            </div>
          </div>
        </div>
        <p className="text-[11px] text-gray-400">Seleccionado: <code className="font-mono">{config.avatarColor}</code></p>
      </section>

      {/* Instrucción personalizada */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-violet-500" />
          Instrucción adicional (opcional)
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Texto que se agrega al system prompt del asistente en cada sesión.
          Úsalo para agregar contexto organizacional específico.
        </p>
        <textarea
          value={config.customInstruction}
          onChange={e => updateField('customInstruction', e.target.value)}
          placeholder={`Ej: "Esta organización es un hospital público de alta complejidad. Enfoca las respuestas en el contexto de salud pública y cumplimiento regulatorio del Ministerio de Salud de Chile."`}
          rows={4}
          maxLength={1000}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
        />
        <p className="text-[11px] text-gray-400 text-right">{config.customInstruction.length}/1000</p>
      </section>

      {/* Botones */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleReset}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <RotateCcw className="w-4 h-4" />
          Restablecer valores predeterminados
        </button>
        <button
          onClick={handleSave}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
            saved
              ? 'bg-green-600 text-white'
              : 'bg-violet-600 hover:bg-violet-700 text-white'
          }`}
        >
          <Save className="w-4 h-4" />
          {saved ? 'Guardado' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
